import { useEffect, useState } from 'react';
import { DifferenceProps } from '../DifferenceElementView';
import DifferenceDescription from '../DifferenceDescription/DifferenceDescription';
import { useSelector } from 'react-redux';
import { getIsStepAndRepeat, getSelectedModule, getThumbnailsImagesPath } from 'store';
import { CTFModuleTypes, DifferenceTypeNames, DocumentTypes } from 'types';
import Difference from 'pdftron/docManager/Difference';
import { imageReadyCache } from './graphicThumbnailReadyCache';
import DifferenceCardThumbnail from './DifferenceCardThumbnail';
import DifferenceCardGraphics from './DifferenceCardGraphics';
import { graphicThumbnailStatus, loadGraphicThumbnail } from './graphicThumbnailLoad';

const GraphicDifference = ({ difference, multipleSelectionMode = false }: DifferenceProps) => {
  const isStepAndRepeat = useSelector(getIsStepAndRepeat);
  const selectedModule = useSelector(getSelectedModule);
  const sourcedifferenceThumbnailBasePath = useSelector(getThumbnailsImagesPath('master'));
  const targetdifferenceThumbnailBasePath = useSelector(getThumbnailsImagesPath('sample'));
  const isEmbeddedImage = difference.type === DifferenceTypeNames.EmbeddedImage;
  const differenceNumber = isEmbeddedImage ? difference.matchIndex : difference.differenceIndex;
  const confidenceLevel = difference.embeddedImageDetails?.matchDetails[0]?.confidenceLevel;
  const userLinkedMatch = difference.embeddedImageDetails?.userLinkedMatch;
  const hasNoMatch = confidenceLevel === 'None' && !userLinkedMatch;

  // Determine if this is a sample->master deletion (image exists in sample but not in master)
  const embeddedImageDeletionTarget = isEmbeddedImage ? Difference.getEmbeddedImageDeletionTarget(difference) : null;
  const isSampleToMasterDeletion = embeddedImageDeletionTarget === 'source';

  // User-drawn side: flag when present, else same coordinate fallback as getEmbeddedImageDeletionTarget (older diffs)
  const userDrawnSide = userLinkedMatch ? Difference.getEmbeddedImageDeletionMarkerSide(difference) : null;

  const sourceThumbnailPath =
    userDrawnSide === 'source'
      ? (difference.thumbnailPaths?.source?.thumbnailUrl ?? '')
      : `${sourcedifferenceThumbnailBasePath}${differenceNumber}.png`;
  const targetThumbnailPath =
    userDrawnSide === 'target'
      ? (difference.thumbnailPaths?.target?.thumbnailUrl ?? '')
      : `${targetdifferenceThumbnailBasePath}${differenceNumber}.png`;

  const sourceThumbnailLoading =
    userDrawnSide === 'source' && difference.thumbnailPaths?.source?.thumbnailUrl === undefined;
  const targetThumbnailLoading =
    userDrawnSide === 'target' && difference.thumbnailPaths?.target?.thumbnailUrl === undefined;
  const sourceThumbnailFailed = userDrawnSide === 'source' && difference.thumbnailPaths?.source?.thumbnailUrl === null;
  const targetThumbnailFailed = userDrawnSide === 'target' && difference.thumbnailPaths?.target?.thumbnailUrl === null;

  const sourceHasImage = userLinkedMatch ? true : !hasNoMatch || !isSampleToMasterDeletion;
  const targetHasImage = userLinkedMatch ? true : !hasNoMatch || isSampleToMasterDeletion;

  const [sourceReady, setSourceReady] = useState(() => {
    if (!sourceHasImage || !sourceThumbnailPath) return !sourceHasImage;
    return !!imageReadyCache[sourceThumbnailPath];
  });
  const [targetReady, setTargetReady] = useState(() => {
    if (!targetHasImage || !targetThumbnailPath) return !targetHasImage;
    return !!imageReadyCache[targetThumbnailPath];
  });
  const [sourceLoadFailed, setSourceLoadFailed] = useState(false);
  const [targetLoadFailed, setTargetLoadFailed] = useState(false);
  const sourcePageNumberText = `source (p${difference.source.location.pageNumber})`;
  const targetPageNumberText =
    isStepAndRepeat && selectedModule === CTFModuleTypes.graphics && difference.matchIndex !== undefined
      ? `new (Repeat ${difference.matchIndex + 1})`
      : `new (p${difference.target.location.pageNumber})`;

  useEffect(() => {
    let cancelled = false;
    let sourceRetryTimeoutId: ReturnType<typeof setTimeout> | undefined;
    let targetRetryTimeoutId: ReturnType<typeof setTimeout> | undefined;

    const scheduleSourceRetry = (fn: () => void, delay: number) => {
      if (sourceRetryTimeoutId !== undefined) clearTimeout(sourceRetryTimeoutId);
      sourceRetryTimeoutId = setTimeout(fn, delay);
    };
    const scheduleTargetRetry = (fn: () => void, delay: number) => {
      if (targetRetryTimeoutId !== undefined) clearTimeout(targetRetryTimeoutId);
      targetRetryTimeoutId = setTimeout(fn, delay);
    };

    if (!sourceHasImage || !sourceThumbnailPath) {
      setSourceLoadFailed(false);
      setSourceReady(!sourceThumbnailPath ? false : true);
    } else if (imageReadyCache[sourceThumbnailPath]) {
      setSourceLoadFailed(false);
      setSourceReady(true);
    } else {
      setSourceReady(false);
      setSourceLoadFailed(false);
      loadGraphicThumbnail({
        thumbnailPath: sourceThumbnailPath,
        useCors: userDrawnSide === 'source',
        scheduleRetry: scheduleSourceRetry,
        isCancelled: () => cancelled,
        onReady: () => {
          setSourceReady(true);
          imageReadyCache[sourceThumbnailPath] = true;
        },
        onFailed: () => setSourceLoadFailed(true),
      });
    }

    if (!targetHasImage || !targetThumbnailPath) {
      setTargetLoadFailed(false);
      setTargetReady(!targetThumbnailPath ? false : true);
    } else if (imageReadyCache[targetThumbnailPath]) {
      setTargetLoadFailed(false);
      setTargetReady(true);
    } else {
      setTargetReady(false);
      setTargetLoadFailed(false);
      loadGraphicThumbnail({
        thumbnailPath: targetThumbnailPath,
        useCors: userDrawnSide === 'target',
        scheduleRetry: scheduleTargetRetry,
        isCancelled: () => cancelled,
        onReady: () => {
          setTargetReady(true);
          imageReadyCache[targetThumbnailPath] = true;
        },
        onFailed: () => setTargetLoadFailed(true),
      });
    }

    return () => {
      cancelled = true;
      if (sourceRetryTimeoutId !== undefined) clearTimeout(sourceRetryTimeoutId);
      if (targetRetryTimeoutId !== undefined) clearTimeout(targetRetryTimeoutId);
    };
  }, [sourceThumbnailPath, targetThumbnailPath, sourceHasImage, targetHasImage, userDrawnSide]);

  const thumbnailAlt = (documentType: DocumentTypes) =>
    `${documentType.charAt(0).toUpperCase() + documentType.slice(1)} Difference # ${differenceNumber}`;

  const leftThumbnail = (
    <DifferenceCardThumbnail
      status={graphicThumbnailStatus({
        documentHasImage: sourceHasImage,
        isReady: sourceReady,
        isFailed: sourceThumbnailFailed || sourceLoadFailed,
      })}
      src={sourceThumbnailPath}
      alt={thumbnailAlt(DocumentTypes.source)}
      imgId={`source-thumbnail-${differenceNumber}`}
      credentialed={userDrawnSide === 'source'}
    />
  );

  const rightThumbnail = (
    <DifferenceCardThumbnail
      status={graphicThumbnailStatus({
        documentHasImage: targetHasImage,
        isReady: targetReady,
        isFailed: targetThumbnailFailed || targetLoadFailed,
      })}
      src={targetThumbnailPath}
      alt={thumbnailAlt(DocumentTypes.target)}
      imgId={`target-thumbnail-${differenceNumber}`}
      credentialed={userDrawnSide === 'target'}
    />
  );

  return (
    <DifferenceDescription
      difference={difference}
      media={
        <DifferenceCardGraphics
          source={leftThumbnail}
          target={rightThumbnail}
          sourceLabel={sourcePageNumberText}
          targetLabel={targetPageNumberText}
          isFound={!hasNoMatch}
        />
      }
      multipleSelectionMode={multipleSelectionMode}
    />
  );
};

export default GraphicDifference;
